import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { useBalance } from '../../../account/hooks/use-balance';
import { useWalletTotals } from '../../../account/hooks/use-wallet-totals';
import { CompactNavigationBar } from '../../../gameplay/components/compact-navigation-bar/compact-navigation-bar';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { ContextLink } from '../../../shared/components/context-link/context-link';
import { useCurrentCurrency } from '../../../shared/hooks/use-current-currency';
import { useFullPath } from '../../../shared/hooks/use-full-location';
import { useSignIn } from '../../../shared/hooks/use-sign-in';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';

import styles from './side-navigation.module.scss';

const style = createStyleHelper(styles, 'side-navigation-component');

export interface ITransactionsProps {
    className?: string;
    wallets?: Array<{
        type?: string | null;
        currencyCode?: string | null;
        balanceAmount?: number | null;
    }>;
    onCloseNav?: () => void;
}

export const SideNavigationComponent: FunctionComponent<ITransactionsProps> = ({
    className,
    onCloseNav,
}) => {
    const auth = useAuth();
    const isLoggedIn = !!auth.isAuthenticated && !!auth.user;
    const intl = useIntl();
    const returnPath = useFullPath();

    const menuSections = useMemo(
        () => [
            {
                requireLogin: true,
                id: 'overview',
                translationKey: 'side-navigation.user-info.my-account',
                defaultDisplayName: 'My Account',
                href: '/account/overview',
            },
            {
                id: 'games',
                translationKey: 'side-navigation.user-info.home',
                defaultDisplayName: 'Home',
                href: '/sports',
            },
            // {
            //     id: 'crab-club',
            //     translationKey: 'side-navigation.user-info.games',
            //     defaultDisplayName: 'Crab Club',
            //     href: '/crab-club',
            // },
            // {
            //     id: 'splash',
            //     translationKey: 'side-navigation.user-info.splash',
            //     defaultDisplayName: 'Free Pickem',
            //     href: '/pickem',
            // },
            {
                id: 'merch',
                translationKey: 'side-navigation.user-info.merch',
                defaultDisplayName: 'Merchandise',
                href: '/merchandise',
            },
            {
                id: 'promo',
                translationKey: 'side-navigation.user-info.promo',
                defaultDisplayName: 'Promotions',
                href: '/promo',
            },
            // {
            //     id: 'notifications',
            //     translationKey: 'side-navigation.user-info.notifications',
            //     defaultDisplayName: 'Notifications',
            //     href: '',
            //     onclick: onNotificationClick,
            // },
            {
                requireLogin: true,
                id: 'logout',
                translationKey: 'side-navigation.user-info.logout',
                defaultDisplayName: 'Logout',
                href: `/logout?backTo=${returnPath}`,
            },
        ],
        [returnPath],
    );

    const selectedCurrency = useCurrentCurrency();
    const { wallets } = useBalance();
    const totalBalance = useWalletTotals(wallets);
    const signIn = useSignIn();

    function onNotificationClick() {
        const item = document.getElementById('webpush-notification-center');

        if (item) {
            item.style.display = 'block';
        }
    }

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('navigation', { divided: isLoggedIn })}>
                <CompactNavigationBar
                    onClose={onCloseNav}
                    title={isLoggedIn ? auth.user?.profile?.family_name : null}
                />
            </div>
            {/*{!isLoggedIn ? (*/}
            {/*    <div className={style('actions')} onClick={onCloseNav}>*/}
            {/*        <Button*/}
            {/*            className={style('cta')}*/}
            {/*            hollow*/}
            {/*            size={ButtonSize.LARGE}*/}
            {/*            onClick={signIn}*/}
            {/*        >*/}
            {/*            Login*/}
            {/*        </Button>*/}
            {/*        <Button className={style('cta')} size={ButtonSize.LARGE} href="/registration">*/}
            {/*            Sign Up*/}
            {/*        </Button>*/}
            {/*    </div>*/}
            {/*) : null}*/}

            <nav className={style('menu-list', { loggedin: isLoggedIn })} onClick={onCloseNav}>
                {menuSections.map((item) => {
                    const label = (
                        <FormattedMessage
                            id={item.translationKey}
                            defaultMessage={item.defaultDisplayName}
                        />
                    );

                    if (item.requireLogin && !isLoggedIn) {
                        return;
                    }

                    // if (item.onclick) {
                    //     return (
                    //         <a key={item.id} className={style('link')} onClick={item.onclick}>
                    //             {label}
                    //         </a>
                    //     );
                    // }

                    return (
                        <ContextLink key={item.id} className={style('link')} to={item.href}>
                            {label}
                        </ContextLink>
                    );
                })}
            </nav>

            {isLoggedIn ? (
                <div className={style('footer')}>
                    <ContextLink
                        to={'/account/overview'}
                        onClick={onCloseNav}
                        className={style('link')}
                    >
                        <div className={style('balance')}>
                            <div className={style('balance-text')}>
                                <FormattedMessage
                                    id="side-navigation.balance"
                                    defaultMessage="Balance"
                                />
                            </div>
                            <div className={style('balance-value')}>
                                {typeof totalBalance !== 'undefined'
                                    ? intl.formatNumber(totalBalance.total.value, {
                                          style: 'currency',
                                          currency: selectedCurrency,
                                      })
                                    : ''}
                            </div>
                        </div>
                    </ContextLink>
                    <div className={style('button-section')}>
                        <Button
                            className={style('button-item')}
                            size={ButtonSize.SMALL}
                            href="/cashier/deposit"
                            onClick={onCloseNav}
                        >
                            <FormattedMessage
                                id="side-navigation.deposit"
                                defaultMessage="DEPOSIT"
                            />
                        </Button>
                        <Button
                            className={style('button-item')}
                            size={ButtonSize.SMALL}
                            hollow
                            href="/cashier/withdrawal"
                            onClick={onCloseNav}
                        >
                            <FormattedMessage
                                id="side-navigation.withdraw"
                                defaultMessage="WITHDRAW"
                            />
                        </Button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
